import { datadogLogs } from '@datadog/browser-logs';
import Cookie from 'js-cookie';
import { useCallback, useMemo } from 'react';
import { fetchData } from 'utils';
import useError from 'hooks/useError';
import useSmartlook from 'hooks/useSmartlook';
import { isProdEnv } from 'utils/isDevEnv';

// Regex provided by BE to check if GA cookies are valid
const BE_GA_REGEX = /^GA\d\.\d+\.\d+\.\d+$/;
const BE_GA_MEASUREMENT_REGEX =
  /^GS\d\.\d+\.s\d+\$o\d+\$g\d+\$t\d+\$j\d+\$l\d+\$h\d+(\$d[a-zA-Z\d]{34})?$/;

// TODO: Refactor to react query
const useGACookies = () => {
  const { showError } = useError();
  const { error } = useSmartlook();

  const getAnalyticsCookies = useCallback(() => {
    const analyticsCookies = [];

    // Measurement cookie name
    const measurementId = import.meta.env.VITE_GA_MEASUREMENT_ID || '';
    const [, secondCookieName] = measurementId.split('-');

    // Cookies values
    const gaValue = Cookie.get('_ga') || '';
    const ga4Value = Cookie.get(`_ga_${secondCookieName}`) || '';

    if (BE_GA_REGEX.test(gaValue)) {
      analyticsCookies.push({
        name: 'ga',
        value: gaValue,
      });
    }

    if (BE_GA_MEASUREMENT_REGEX.test(ga4Value)) {
      analyticsCookies.push({
        name: `ga_${secondCookieName}`,
        value: ga4Value,
      });
    }

    // log to investigate how many clients don't have valid cookies on PROD
    if (analyticsCookies.length < 2 && isProdEnv()) {
      datadogLogs.logger.error(
        `gacookies missing. ga - ${gaValue} + ga_${secondCookieName} - ${ga4Value}`,
      );
    }

    return analyticsCookies;
  }, []);

  const sendGACookies = useCallback(async () => {
    const gaCookies = getAnalyticsCookies();

    if (!gaCookies.length) return;

    try {
      const data = await fetchData('/client/cookies', {
        body: JSON.stringify(gaCookies),
        headers: {
          'Content-Type': 'application/vnd.analytics.v2+json',
        },
        method: 'post',
      });

      return data;
    } catch (e) {
      error('[useGACookies][sendGACookies]', e);
    }
  }, [showError]);

  return useMemo(() => {
    return {
      getAnalyticsCookies,
      sendGACookies,
    };
  }, [getAnalyticsCookies, sendGACookies]);
};

export default useGACookies;
